'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { User, Mail, Phone, Building2, ChevronRight, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { entriesService } from '@/lib/services/entriesService';
import { consentSettingsService, ConsentSettings } from '@/lib/services/consentSettingsService';

interface SubmissionFormProps {
  onSubmit: (entry: { name: string; email: string; phone: string; company: string }, entryId: string, raffleCode?: string) => void;
}

interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
}

export default function SubmissionForm({ onSubmit }: SubmissionFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentSettings, setConsentSettings] = useState<ConsentSettings | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [trialEapOptin, setTrialEapOptin] = useState(false);

  useEffect(() => {
    consentSettingsService.getSettings().then((s) => {
      setConsentSettings(s);
    }).catch(() => {
      // If service fails, don't block the form
    });
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>({ mode: 'onBlur' });

  const onFormSubmit = async (values: FormValues) => {
    // Check consent if enabled
    if (consentSettings?.enabled && !consentChecked) {
      setConsentError(true);
      return;
    }

    setIsSubmitting(true);
    try {
      // Check for duplicate email
      const emailExists = await entriesService.checkEmailExists(values.email);
      if (emailExists) {
        setError('email', { type: 'manual', message: 'This email address has already been used to enter.' });
        toast.error('This email address has already been entered.');
        setIsSubmitting(false);
        return;
      }

      const result = await entriesService.createEntry({
        name: values.name,
        email: values.email,
        phone: values.phone,
        company: values.company,
        trialEapOptin,
      });

      toast.success('Details saved! Now choose your game 🎮');
      onSubmit(
        { name: values.name, email: values.email, phone: values.phone, company: values.company },
        result.id,
        result.raffleCode
      );
    } catch (err: any) {
      toast.error(`Failed to save entry: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render consent text with linked "Privacy Policy" and "Terms of Service"
  const renderConsentText = () => {
    if (!consentSettings) return null;
    const { consentText, privacyPolicyUrl, termsUrl } = consentSettings;
    const parts = consentText.split(/(Privacy Policy|Terms of Service)/g);
    return parts.map((part, i) => {
      if (part === 'Privacy Policy') {
        return (
          <a
            key={i}
            href={privacyPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-600 underline font-semibold hover:text-green-700"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
          </a>
        );
      }
      if (part === 'Terms of Service') {
        return (
          <a
            key={i}
            href={termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-600 underline font-semibold hover:text-green-700"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-green-500 rounded-2xl shadow-lg mb-3">
          <User size={28} className="text-white" />
        </div>
        <h2 className="text-2xl font-extrabold text-gray-800">Enter Your Details</h2>
        <p className="text-gray-500 text-sm mt-1">
          Fill in your info to get started
        </p>
      </div>

      <div className="bg-white rounded-3xl shadow-xl border border-green-100 p-6">
        <form onSubmit={handleSubmit(onFormSubmit)} noValidate className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="name">
              Full Name <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <User size={16} />
              </div>
              <input
                id="name"
                type="text"
                placeholder="Sarah Mitchell"
                className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors duration-150 outline-none focus:ring-2 focus:ring-green-500/30 ${
                  errors.name
                    ? 'border-red-300 bg-red-50' :'border-gray-200 bg-gray-50 focus:border-green-400 focus:bg-white'
                }`}
                {...register('name', {
                  required: 'Full name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' },
                })}
              />
            </div>
            {errors.name && (
              <p className="mt-1 text-xs text-red-500 font-medium">{errors.name.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="email">
              Email Address <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Mail size={16} />
              </div>
              <input
                id="email"
                type="email"
                placeholder="sarah@company.com"
                className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors duration-150 outline-none focus:ring-2 focus:ring-green-500/30 ${
                  errors.email
                    ? 'border-red-300 bg-red-50' :'border-gray-200 bg-gray-50 focus:border-green-400 focus:bg-white'
                }`}
                {...register('email', {
                  required: 'Email address is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-xs text-red-500 font-medium">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="phone">
              Phone Number <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Phone size={16} />
              </div>
              <input
                id="phone"
                type="tel"
                placeholder="+1 (555) 234-5678"
                className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors duration-150 outline-none focus:ring-2 focus:ring-green-500/30 ${
                  errors.phone
                    ? 'border-red-300 bg-red-50' :'border-gray-200 bg-gray-50 focus:border-green-400 focus:bg-white'
                }`}
                {...register('phone', {
                  required: 'Phone number is required',
                  minLength: { value: 7, message: 'Enter a valid phone number' },
                })}
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-xs text-red-500 font-medium">{errors.phone.message}</p>
            )}
          </div>

          {/* Company */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="company">
              Company / Organisation <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Building2 size={16} />
              </div>
              <input
                id="company"
                type="text"
                placeholder="Acme Corp"
                className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors duration-150 outline-none focus:ring-2 focus:ring-green-500/30 ${
                  errors.company
                    ? 'border-red-300 bg-red-50' :'border-gray-200 bg-gray-50 focus:border-green-400 focus:bg-white'
                }`}
                {...register('company', {
                  required: 'Company name is required',
                  minLength: { value: 2, message: 'Company name must be at least 2 characters' },
                })}
              />
            </div>
            {errors.company && (
              <p className="mt-1 text-xs text-red-500 font-medium">{errors.company.message}</p>
            )}
          </div>

          {/* Trial / EAP Opt-in */}
          <div className="p-3 rounded-xl border border-purple-200 bg-purple-50">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  id="trial-eap-checkbox"
                  checked={trialEapOptin}
                  onChange={(e) => setTrialEapOptin(e.target.checked)}
                  className="absolute opacity-0 w-5 h-5 cursor-pointer"
                  style={{ zIndex: 1 }}
                />
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all pointer-events-none ${
                    trialEapOptin ? 'bg-purple-600 border-purple-600' : 'border-gray-300 bg-white'
                  }`}
                >
                  {trialEapOptin && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-purple-700 mb-0.5">🚀 Free Trial / Early Access Program (EAP)</p>
                <p className="text-xs text-purple-600 leading-relaxed">
                  I'm interested in signing up for a free trial or joining the Early Access Program.
                </p>
              </div>
            </label>
          </div>

          {/* Consent Checkbox */}
          {consentSettings?.enabled && (
            <div className={`p-3 rounded-xl border transition-colors ${
              consentError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
            }`}>
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <div className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    id="consent-checkbox"
                    checked={consentChecked}
                    onChange={(e) => {
                      setConsentChecked(e.target.checked);
                      if (e.target.checked) setConsentError(false);
                    }}
                    className="absolute opacity-0 w-5 h-5 cursor-pointer"
                    style={{ zIndex: 1 }}
                  />
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all pointer-events-none ${
                      consentChecked
                        ? 'bg-green-600 border-green-600'
                        : consentError
                        ? 'border-red-400 bg-white' :'border-gray-300 bg-white'
                    }`}
                  >
                    {consentChecked && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <ShieldCheck size={13} className="text-green-600 flex-shrink-0" />
                    <span className="text-xs font-semibold text-gray-600">Required</span>
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    {renderConsentText()}
                  </p>
                </div>
              </label>
              {consentError && (
                <p className="mt-2 text-xs text-red-500 font-medium pl-8">
                  Please tick this box to continue
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400">
            <span className="text-red-400">*</span> All fields are required
          </p>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 active:scale-95 text-white font-bold text-base py-4 rounded-2xl shadow-lg shadow-green-200 transition-all duration-150 flex items-center justify-center gap-2"
            style={{ minHeight: '56px' }}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span>Continue to Game</span>
                <ChevronRight size={20} />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}